import { ReactNode, useRef, useState } from 'react';
import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
    CalendarDays,
    LockKeyhole,
    MapPin,
    Repeat2,
    Users,
} from 'lucide-react';

import { CloseIconSVG } from 'src/assets/svg';
import { Button } from 'src/components/Buttons/Button';
import { AppModal } from 'src/components/Modals/AppModal';
import {
    EventRecurrenceFrequency,
    EventStatus,
    EventType,
    getEventTypeColor,
    getMunduEventType,
    MunduEvent,
} from 'src/domain/event';
import { EventIconGraphic } from './EventIconGraphic';
import { EventOrganizer } from './EventOrganizer';
import { EventAudienceCounts } from './EventAudienceCounts';
import { EventResponseStatusPanel } from './EventResponseStatus';
import { EventDetailsTabs } from './EventDetailsTabs';
import { EventDetailsTabPanel } from './EventDetailsTabPanel';
import { useEventResponseStatus } from './useEventResponseStatus';
import {
    EventReadOnlyReason,
    getOriginalOccurrenceStart,
} from './eventFormModel';

const formatReadOnlyDate = (value: string, locale: string) =>
    new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(parseISO(value));

const formatReadOnlyTime = (value: string, locale: string) =>
    new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).format(parseISO(value));

const ReadOnlyDetailRow = ({
    icon: Icon,
    label,
    value,
    secondaryValue,
}: {
    icon: typeof CalendarDays;
    label: string;
    value: ReactNode;
    secondaryValue?: ReactNode;
}) => (
    <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-lightPeach text-woodyBrown">
            <Icon className="h-5 w-5" strokeWidth={1.8} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1 pt-0.5">
            <p className="text-xs font-semibold text-woodyBrown/55">{label}</p>
            <div className="mt-0.5 whitespace-pre-line leading-6">{value}</div>
            {secondaryValue ? (
                <div className="text-sm leading-5 text-woodyBrown/60">
                    {secondaryValue}
                </div>
            ) : null}
        </div>
    </div>
);

const EventReadOnlyDetails = ({
    event,
    reason,
    attendeeLabels,
    canEdit,
    canRestore,
}: {
    event: MunduEvent;
    reason: EventReadOnlyReason;
    attendeeLabels: string[];
    canEdit: boolean;
    canRestore: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const start = parseISO(event.startDate);
    const end = parseISO(event.endDate);
    const isSameDay = start.toDateString() === end.toDateString();
    const date = isSameDay
        ? formatReadOnlyDate(event.startDate, locale)
        : `${formatReadOnlyDate(
              event.startDate,
              locale,
          )} – ${formatReadOnlyDate(event.endDate, locale)}`;
    const time = event.allDay
        ? t('modules.calendar.form.allDayLabel')
        : `${formatReadOnlyTime(
              event.startDate,
              locale,
          )} – ${formatReadOnlyTime(event.endDate, locale)}`;
    const recurrenceDate = parseISO(event.seriesStartDate ?? event.startDate);
    const recurrence = event.recurrence
        ? event.recurrence.frequency === EventRecurrenceFrequency.daily
            ? t('modules.calendar.form.recurrenceDaily')
            : event.recurrence.frequency === EventRecurrenceFrequency.weekly
            ? t('modules.calendar.form.recurrenceWeeklyOn', {
                  weekday: recurrenceDate.toLocaleDateString(locale, {
                      weekday: 'long',
                  }),
              })
            : t('modules.calendar.form.recurrenceMonthlyOnDay', {
                  day: recurrenceDate.getDate(),
              })
        : undefined;
    const recurrenceDescription = event.recurrence
        ? `${recurrence} · ${t(
              'modules.calendar.form.recurrenceUntilLabel',
          )} ${formatReadOnlyDate(event.recurrence.until, locale)}`
        : undefined;
    const organizerName =
        event.createdByName?.trim() ||
        t('modules.calendar.form.organizerUnknown');
    const eventType = getMunduEventType(event);
    const eventColor = getEventTypeColor(eventType);
    const isCancelled = event.status === EventStatus.cancelled;

    return (
        <div className="flex flex-1 flex-col">
            <div className="flex-1 px-5 py-5 sm:px-8 sm:py-6">
                <div className="mx-auto max-w-3xl space-y-4">
                    <section
                        className={cx(
                            'overflow-hidden rounded-2xl border-2 bg-white p-5 shadow-sm sm:p-6',
                            eventType === EventType.internal && 'border-dotted',
                            isCancelled && 'border-solid bg-[#f3f0f1]',
                        )}
                        style={{
                            borderColor: isCancelled
                                ? '#c9c0c4'
                                : eventColor.background,
                        }}
                    >
                        <div className="flex items-start gap-4">
                            <span
                                className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full"
                                style={{
                                    backgroundColor: isCancelled
                                        ? '#e2dcdf'
                                        : eventColor.background,
                                    color: isCancelled
                                        ? '#6f5b65'
                                        : eventColor.foreground,
                                }}
                            >
                                {event.icon ? (
                                    <EventIconGraphic
                                        icon={event.icon}
                                        className="h-6 w-6"
                                    />
                                ) : (
                                    <CalendarDays
                                        className="h-6 w-6"
                                        strokeWidth={1.8}
                                        aria-hidden="true"
                                    />
                                )}
                            </span>
                            <div className="min-w-0 flex-1">
                                <h3 className="break-words font-logo text-2xl leading-tight">
                                    {event.title}
                                    {isCancelled
                                        ? ` – ${t(
                                              'modules.calendar.form.cancelledLabel',
                                          )}`
                                        : ''}
                                </h3>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <span
                                        className="rounded-full px-3 py-1 font-body text-sm font-normal leading-5 tracking-normal"
                                        style={{
                                            backgroundColor:
                                                eventColor.background,
                                            color: eventColor.foreground,
                                        }}
                                    >
                                        {t(
                                            `modules.calendar.form.types.${eventType}.label`,
                                        )}
                                    </span>
                                    {event.recurrence ? (
                                        <span className="inline-flex items-center gap-2 rounded-full bg-lightPeach px-3 py-1 font-body text-sm font-normal leading-5 tracking-normal">
                                            <Repeat2
                                                className="h-3.5 w-3.5"
                                                strokeWidth={1.8}
                                                aria-hidden="true"
                                            />
                                            {t(
                                                'modules.calendar.views.recurringEvent',
                                            )}
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="grid gap-5 rounded-2xl bg-white p-5 shadow-sm sm:grid-cols-2 sm:p-6">
                        <div className="sm:col-span-2">
                            <EventOrganizer
                                label={t(
                                    'modules.calendar.form.organizerLabel',
                                )}
                                name={organizerName}
                                avatarUrl={event.createdByAvatarUrl}
                                bordered={false}
                            />
                        </div>
                        <ReadOnlyDetailRow
                            icon={CalendarDays}
                            label={t(
                                'modules.calendar.form.scheduleSectionTitle',
                            )}
                            value={date}
                            secondaryValue={time}
                        />
                        {event.location ? (
                            <ReadOnlyDetailRow
                                icon={MapPin}
                                label={t('modules.calendar.form.locationLabel')}
                                value={event.location}
                            />
                        ) : null}
                        {recurrenceDescription ? (
                            <ReadOnlyDetailRow
                                icon={Repeat2}
                                label={t(
                                    'modules.calendar.form.recurrenceLabel',
                                )}
                                value={recurrenceDescription}
                            />
                        ) : null}
                        {eventType !== EventType.internal ||
                        attendeeLabels.length > 0 ? (
                            <ReadOnlyDetailRow
                                icon={Users}
                                label={t(
                                    'modules.calendar.form.attendeesLabel',
                                )}
                                value={
                                    attendeeLabels.length ? (
                                        attendeeLabels.join(', ')
                                    ) : (
                                        <EventAudienceCounts
                                            counts={event}
                                            eventType={eventType}
                                        />
                                    )
                                }
                                secondaryValue={
                                    attendeeLabels.length &&
                                    eventType !== EventType.internal ? (
                                        <EventAudienceCounts
                                            counts={event}
                                            eventType={eventType}
                                        />
                                    ) : undefined
                                }
                            />
                        ) : null}
                    </section>

                    {event.description ? (
                        <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
                            <h3 className="font-semibold">
                                {t('modules.calendar.form.descriptionLabel')}
                            </h3>
                            <p className="mt-2 whitespace-pre-wrap leading-6 text-woodyBrown/80">
                                {event.description}
                            </p>
                        </section>
                    ) : null}

                    {isCancelled ? (
                        <div className="rounded-2xl border border-woodyBrown/15 bg-[#f3f0f1] px-4 py-3 text-sm text-woodyBrown/70">
                            {t(
                                canRestore
                                    ? 'modules.calendar.form.cancelledManageNotice'
                                    : 'modules.calendar.form.cancelledNotice',
                            )}
                        </div>
                    ) : !canEdit ? (
                        <div className="flex items-start gap-3 rounded-2xl border border-woodyBrown/15 bg-white bg-opacity-50 px-4 py-3 text-woodyBrown/70">
                            <LockKeyhole
                                className="mt-0.5 h-5 w-5 flex-shrink-0 text-woodyBrown/50"
                                strokeWidth={1.8}
                                aria-hidden="true"
                            />
                            <div>
                                <p className="text-sm font-semibold text-woodyBrown/75">
                                    {t('modules.calendar.form.readOnlyLabel')}
                                </p>
                                <p className="mt-0.5 text-sm leading-5 text-woodyBrown/60">
                                    {t(
                                        reason === 'moduleMembership'
                                            ? 'modules.calendar.form.moduleMembershipReadOnlyNotice'
                                            : 'modules.calendar.form.readOnlyNotice',
                                    )}
                                </p>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

type EventReadOnlyModalProps = {
    show: boolean;
    event: MunduEvent;
    reason: EventReadOnlyReason;
    attendeeLabels: string[];
    canEdit: boolean;
    canDelete: boolean;
    canRestore: boolean;
    isRestoring: boolean;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onRestore: () => void;
};

const EventReadOnlyActions = ({
    canEdit,
    canDelete,
    canRestore,
    isRestoring,
    onClose,
    onEdit,
    onDelete,
    onRestore,
}: Pick<
    EventReadOnlyModalProps,
    | 'canEdit'
    | 'canDelete'
    | 'canRestore'
    | 'isRestoring'
    | 'onClose'
    | 'onEdit'
    | 'onDelete'
    | 'onRestore'
>) => {
    const { t } = useTranslation();
    return (
        <footer className="flex flex-shrink-0 overflow-hidden border-t border-woodyBrown/15 bg-lightPeach px-5 py-4 [scrollbar-gutter:stable] sm:px-8">
            <div className="mx-auto flex w-full max-w-3xl justify-end gap-3">
                <Button
                    title={t('common.close')}
                    onClick={onClose}
                    disabled={isRestoring}
                    size="comfortable"
                />
                {canEdit ? (
                    <Button
                        title={t('common.edit')}
                        onClick={onEdit}
                        active
                        size="comfortable"
                    />
                ) : (
                    <>
                        {canDelete ? (
                            <Button
                                title={t('modules.calendar.form.delete')}
                                onClick={onDelete}
                                disabled={isRestoring}
                                size="comfortable"
                            />
                        ) : null}
                        {canRestore ? (
                            <Button
                                title={t('modules.calendar.form.restoreEvent')}
                                onClick={onRestore}
                                disabled={isRestoring}
                                active
                                size="comfortable"
                            />
                        ) : null}
                    </>
                )}
            </div>
        </footer>
    );
};

const EventReadOnlyModalContent = ({
    show,
    event,
    reason,
    attendeeLabels,
    canEdit,
    canDelete,
    canRestore,
    isRestoring,
    onClose,
    onEdit,
    onDelete,
    onRestore,
}: EventReadOnlyModalProps) => {
    const { t } = useTranslation();
    const [showResponseStatus, setShowResponseStatus] = useState(false);
    const [modalHeight, setModalHeight] = useState<number>();
    const modalRef = useRef<HTMLDivElement>(null);
    const responseStatus = useEventResponseStatus(event, show);
    const preview =
        !responseStatus.error && responseStatus.data
            ? responseStatus.data.counts.totalInvited
                ? t('modules.calendar.responseStatus.replyProgress', {
                      replied:
                          responseStatus.data.counts.going +
                          responseStatus.data.counts.notGoing,
                      total: responseStatus.data.counts.totalInvited,
                  })
                : t('modules.calendar.responseStatus.noInvitees')
            : undefined;
    const selectTab = (responses: boolean) => {
        // Capture Details before hiding it. Both panels then share this space.
        if (responses && !showResponseStatus) {
            if (modalHeight === undefined && modalRef.current) {
                setModalHeight(modalRef.current.getBoundingClientRect().height);
            }
            void responseStatus.mutate();
        }
        setShowResponseStatus(responses);
    };

    return (
        <div className="fixed z-100 inset-0 overflow-y-auto bg-black bg-opacity-75 transition-opacity">
            <AppModal show={show} clickOutside={onClose}>
                <div
                    ref={modalRef}
                    style={{ height: modalHeight }}
                    className="absolute top-1/2 left-1/2 flex max-h-[92vh] w-[min(94vw,900px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-30px bg-lightPeach text-woodyBrown shadow-xl"
                >
                    <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-woodyBrown/15 px-5 py-5 sm:px-8">
                        <h2 className="text-xl font-semibold">
                            {t('modules.calendar.form.viewTitle')}
                        </h2>
                        <button
                            type="button"
                            aria-label={t('common.close')}
                            title={t('common.close')}
                            onClick={onClose}
                            disabled={isRestoring}
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-woodyBrown transition-colors hover:bg-woodyBrown hover:text-lightPeach focus:outline-none"
                        >
                            <CloseIconSVG className="h-3 w-3" />
                        </button>
                    </header>
                    {event.canViewResponseStatus ? (
                        <EventDetailsTabs
                            responsesSelected={showResponseStatus}
                            preview={preview}
                            onSelect={selectTab}
                        />
                    ) : null}
                    <EventDetailsTabPanel
                        tab="details"
                        active={!showResponseStatus}
                        hasTabs={Boolean(event.canViewResponseStatus)}
                    >
                        <EventReadOnlyDetails
                            event={event}
                            reason={reason}
                            attendeeLabels={attendeeLabels}
                            canEdit={canEdit}
                            canRestore={canRestore}
                        />
                    </EventDetailsTabPanel>
                    {event.canViewResponseStatus ? (
                        <EventDetailsTabPanel
                            tab="responses"
                            active={showResponseStatus}
                        >
                            <EventResponseStatusPanel
                                event={event}
                                responseStatus={responseStatus}
                            />
                        </EventDetailsTabPanel>
                    ) : null}
                    <EventReadOnlyActions
                        canEdit={canEdit}
                        canDelete={canDelete}
                        canRestore={canRestore}
                        isRestoring={isRestoring}
                        onClose={onClose}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onRestore={onRestore}
                    />
                </div>
            </AppModal>
        </div>
    );
};

// Unmount each session so reopening always starts on Details with fresh tab state.
export const EventReadOnlyModal = (props: EventReadOnlyModalProps) =>
    props.show ? (
        <EventReadOnlyModalContent
            key={`${props.event.id}:${
                getOriginalOccurrenceStart(props.event) ?? props.event.startDate
            }`}
            {...props}
        />
    ) : null;
